import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCustomContentDetailAPI, updateCustomContentActionsAPI } from "@modules/apis/custom-content";
import { parseEnumType, parseTypeToFieldType } from "@modules/content-schema";
import { setNotification } from "@modules/notification";
import { useTranslation } from "react-i18next";
import useCustomContent from "@hooks/useCustomContent";
import { getContentSchemaDetailAPI } from "@modules/apis/content-schema";
import useCheckPermission from "@hooks/useCheckPermission";
import { getAssetCustomMetadataAPI } from "@modules/apis/asset";
import { TABLE_LIST_TYPE } from "@constants";
import compareVersions, { VERSION_CM_13766_SHARES_V3 } from "@cores/version";
import useHiddenMetadata from "@hooks/useHiddenMetadata";
import { getHiddenCustomMetadataValue } from "@modules/metadataFieldManagements";

const useExtendedCustomMetadata = ({ domain, projectId, resourceId, extendedMetadata }) => {
    const { renderHiddenMetadataPermissionConfirmModal } = useHiddenMetadata();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const { getCustomMetadataOptions, createDefaultValueSchema, checkIsNull } = useCustomContent();
    const CUSTOM_CONTENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "CustomContents" });

    const [customMetadata, setCustomMetadata] = useState([]);
    const [customMetadataInfo, setCustomMetadataInfo] = useState({});

    const targetProjectId = projectId ?? project.id;

    const getCustomMetadata = async ({ contentId }) => {
        try {
            const response = await getCustomContentDetailAPI(stage.endpoint, targetProjectId, contentId);
            const data = response.data;

            const fields = data?.fields?.map((v) => {
                const dataType = v.fieldType?.dataType;
                const newDataType = parseTypeToFieldType(dataType);
                const newField = { ...v, fieldType: { ...v.fieldType, dataType: { ...newDataType } } };
                const parsedField = parseEnumType(newField, "detail");

                return { ...parsedField };
            });
            return fields;
        } catch (error) {
            console.error(error);
        }
    };

    const getAssetCustomMetadata = async ({ assetId, projectId, contentId }) => {
        try {
            const customErrorMessage = true;
            const response = await getAssetCustomMetadataAPI(
                stage.endpoint,
                projectId,
                assetId,
                contentId,
                customErrorMessage,
            );
            const data = response.data;

            const fields = data?.fields?.map((v) => {
                const dataType = v.fieldType?.dataType;
                const newDataType = parseTypeToFieldType(dataType);
                const newField = { ...v, fieldType: { ...v.fieldType, dataType: { ...newDataType } } };
                const parsedField = parseEnumType(newField, "detail");

                return { ...parsedField };
            });
            const customMetadata = { contentSchema: data.contentSchema, fields };
            setCustomMetadataInfo(customMetadata.contentSchema);
            setCustomMetadata(customMetadata.fields);
            return customMetadata;
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const updateCustomMetadata = useCallback(async ({ id, fields }) => {
        try {
            const action = `OVERWRITE_FIELD_VALUES`;
            await updateCustomContentActionsAPI(stage.endpoint, targetProjectId, id, {
                action,
                data: { fields },
            });
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(
                        `common::msg::An error occured while changing Custom Metadata {type}`,
                        `An error occured while changing Custom Metadata {type}`,
                        {
                            type: fields?.[0]?.id,
                        },
                    ),
                }),
            );
        }
    }, []);

    const parseCustomMetadata = useCallback(
        (fields, onChangeAssetData) => {
            const _fields = fields?.map((v) => {
                const dataType = v.fieldType?.dataType;
                if (dataType.detailType) return v;
                const newDataType = parseTypeToFieldType(dataType);
                const newField = { ...v, fieldType: { ...v.fieldType, dataType: { ...newDataType } } };
                const parsedField = parseEnumType(newField, "detail");
                return parsedField;
            });

            return (
                _fields?.map((el) => {
                    const fieldOptions = getCustomMetadataOptions(el);

                    return {
                        ...el,
                        id: el.id,
                        subtitle: el.name,
                        type: el.fieldType.dataType.detailType,
                        value: el.value,
                        options: fieldOptions,
                        placeholder: el.placeholder,
                        validationRule: createDefaultValueSchema(
                            el?.fieldType?.dataType?.type,
                            el?.fieldType?.dataType?.properties?.[0]?.type,
                            el?.fieldType?.dataType?.validation ||
                                el?.fieldType?.dataType?.properties?.[0]?.validation,
                            el?.isRequired,
                            fieldOptions,
                        ),
                        isEditable: el.isEditable,
                        isEditButtonDisabled: !CUSTOM_CONTENTS_PERMISSIONS.EDIT,
                        isHidden: el.isHidden,
                        isRequired: el.isRequired,
                        onSubmitEditValue: (value) => onChangeAssetData && onChangeAssetData(el.id, value),
                        description: el.description,
                    };
                }) ?? []
            );
        },
        [extendedMetadata, customMetadata, CUSTOM_CONTENTS_PERMISSIONS],
    );

    const onHandleUpdateCustomMetadata = useCallback(
        async (id, value) => {
            if (!extendedMetadata) return null;
            await updateCustomMetadata({ id: extendedMetadata.contentId, fields: [{ id: id, value: value }] });

            const fieldIndex = customMetadata?.findIndex((el) => el.id === id);
            if (fieldIndex >= 0) {
                let customMetadata_ = [...customMetadata];
                customMetadata_[fieldIndex].value = value;
                setCustomMetadata(customMetadata_);
            }
        },
        [extendedMetadata, customMetadata],
    );

    const getCustomMetadataDetail = async (extendedMetadata) => {
        if (!extendedMetadata?.contentSchemaId) return;
        try {
            const response = await getContentSchemaDetailAPI(
                stage.id,
                stage.endpoint,
                targetProjectId,
                extendedMetadata.contentSchemaId,
            );
            return { name: response?.data?.name };
        } catch (e) {
            console.error(e);
        }
    };

    const getCustomMetadataHiddenValue = async (fieldId) => {
        try {
            const pendingData = customMetadata.map((el) => (el.id === fieldId ? { ...el, pending: true } : el));
            setCustomMetadata(pendingData);
            const response = await dispatch(getHiddenCustomMetadataValue(domain, extendedMetadata?.contentId, fieldId));
            const successData = customMetadata.map((el) =>
                el.id === fieldId
                    ? {
                          ...response.data,
                          pending: false,
                          error: false,
                          isCheckHiddenValue: true,
                      }
                    : el,
            );
            setCustomMetadata(successData);
        } catch (error) {
            if (error.response?.status === 401) {
                const failData = customMetadata.map((el) =>
                    el.id === fieldId ? { ...el, pending: false, error, isCheckHiddenValue: false } : el,
                );
                setCustomMetadata(failData);
            } else if (error.response?.status === 404) {
                const initialData = customMetadata.map((el) =>
                    el.id === fieldId
                        ? { ...el, pending: false, error: false, value: "", isCheckHiddenValue: true }
                        : el,
                );
                setCustomMetadata(initialData);
            }
        }
    };

    const clearCustomMetadata = () => {
        setCustomMetadata([]);
        setCustomMetadataInfo({});
    };

    useEffect(async () => {
        if (!extendedMetadata || !CUSTOM_CONTENTS_PERMISSIONS.READ) return;
        if (domain === TABLE_LIST_TYPE.ASSET && compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0) {
            //Note: 1.0.119 이상부터는 getAssetCustomMetadata()에 schema name을 내려주므로 실행 안해도 됨
            return;
        }

        const response = await getCustomMetadataDetail(extendedMetadata);
        setCustomMetadataInfo(response);
    }, [extendedMetadata]);

    useEffect(async () => {
        if (!extendedMetadata) {
            if (customMetadata?.length > 0) setCustomMetadata([]);
            return;
        }
        const { contentId } = extendedMetadata;
        if (!contentId || !CUSTOM_CONTENTS_PERMISSIONS.READ) return;

        if (domain === TABLE_LIST_TYPE.ASSET && compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0) {
            try {
                await getAssetCustomMetadata({ assetId: resourceId, projectId, contentId });
            } catch (error) {
                if (error?.response?.status === 400) {
                    dispatch(
                        setNotification({
                            type: "info",
                            contents: t(
                                `common::msg::The custom metadata used in the asset is disabled.`,
                                `The custom metadata used in the asset is disabled.`,
                            ),
                        }),
                    );
                }
            }
        } else {
            try {
                const fields = await getCustomMetadata({ contentId });
                setCustomMetadata(fields);
            } catch (error) {
                if (domain === TABLE_LIST_TYPE.ASSET && error?.response?.status === 400) {
                    dispatch(
                        setNotification({
                            type: "info",
                            contents: t(
                                `common::msg::The custom metadata used in the asset is disabled.`,
                                `The custom metadata used in the asset is disabled.`,
                            ),
                        }),
                    );
                }
            }
        }
    }, [extendedMetadata?.contentId]);

    return {
        getCustomMetadata,
        getAssetCustomMetadata,
        getCustomMetadataDetail,
        clearCustomMetadata,
        parseCustomMetadata,
        updateCustomMetadata,
        customMetadata,
        customMetadataInfo,
        getCustomMetadataHiddenValue,
        renderHiddenMetadataPermissionConfirmModal,
    };
};

export default useExtendedCustomMetadata;
